import React from 'react';
import ExportButton from '../../../components/ExportButton';
import TableFilters from '../../../components/TableFilters';

function TableSection({
  data,
  columns,
  theme,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  onExport,
  showFilters = false,
  showExport = false,
  filters = {},
  onFiltersChange,
  defaultFilters = {},
  sortFieldOptions = []
}) {
  // Ensure pagination values are numbers
  const { currentPage: rawCurrentPage, perPage: rawPerPage, total } = pagination;
  const currentPage = parseInt(rawCurrentPage, 10) || 1;
  const perPage = parseInt(rawPerPage, 10) || 10;
  
  // Calculate total pages with smart validation
  const calculatedPages = Math.ceil(total / perPage);
  
  // Only apply limits if the numbers are clearly wrong (e.g., > 100,000 pages)
  // This allows real data like 3 pages to display correctly
  let totalPages = calculatedPages;
  
  if (calculatedPages > 100000) {
    totalPages = 1000; // Still high but reasonable for debugging
  } else if (calculatedPages > 10000) {
    totalPages = calculatedPages; // Allow high but reasonable numbers
  } else {
    // Normal case - respect the actual data
    totalPages = calculatedPages;
  }
  
  const displayData = data;

  return (
    <>
      {/* Filters and Export Section */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
        {showFilters && (
          <TableFilters
            onFiltersChange={onFiltersChange}
            theme={theme}
            filters={filters}
            defaultFilters={defaultFilters}
            sortFieldOptions={sortFieldOptions}
          />
        )}
        
        {showExport && onExport && (
          <div className="flex-shrink-0">
            <ExportButton
              onExport={onExport}
              label="Export to Excel"
              size="md"
              variant="primary"
            />
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              {columns.map((col, index) => (
                <th
                  key={`row-${index}-col-${col.key}`}
                  className="text-left py-3 px-4 font-semibold"
                  style={{ color: theme.colors.text.primary }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData && displayData.length > 0 ? (
              displayData.map((row, index) => (
                <tr
                  key={row.id || index}
                  className="border-b border-gray-700 hover:bg-gray-800"
                >
                  {columns.map((col) => (
                    <td 
                      key={`${row.id || index}-${col.key}`}
                      className="py-3 px-4"
                      style={{ color: theme.colors.text.primary }}
                    >
                      {col.format ? col.format(row[col.key]) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="py-8 text-center text-gray-400">
                  No data available for this page
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center mt-4 gap-2">
        <div className="flex items-center">
          <span className="mr-2" style={{ color: theme.colors.text.primary }}>Rows per page:</span>
          <select 
            value={perPage} 
            onChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
            className="p-1 border rounded"
            style={{
              backgroundColor: theme.colors.background.primary,
              color: theme.colors.text.primary,
              borderColor: theme.colors.background.accent,
            }}
          >
            {[5, 10, 20, 50].map(num => (
              <option key={num} value={num}>{num}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center">
          <button
            onClick={() => {
              onPageChange(currentPage - 1);
            }}
            disabled={currentPage === 1}
            className="p-2 text-white rounded text-xs disabled:opacity-50"
            style={{
              background: "linear-gradient(270deg, #4B49AC 0.05%, #9999FF 99.97%)",
            }}
          >
            Previous
          </button>
          <span className="mx-2" style={{ color: theme.colors.text.primary }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => {
              onPageChange(currentPage + 1);
            }}
            disabled={currentPage === totalPages}
            className="p-2 text-white rounded text-xs disabled:opacity-50"
            style={{
              background: "linear-gradient(270deg, #4B49AC 0.05%, #9999FF 99.97%)",
            }}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}

export default TableSection;
